
import { useState } from 'react';
import { format, addDays, differenceInDays } from 'date-fns';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/context/UserContext';
import { CycleData } from '@/utils/cycle-tracking';

export const useCircularCycleView = (cycleData: CycleData | null) => {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [startDate, setStartDate] = useState<Date | undefined>(
    cycleData?.periodStartDate ? new Date(cycleData.periodStartDate) : undefined
  );
  const [endDate, setEndDate] = useState<Date | undefined>(
    cycleData?.predictedEndDate ? new Date(cycleData.predictedEndDate) : undefined
  );
  const [activeCalendar, setActiveCalendar] = useState<'start' | 'end'>('start');
  const [highlightedDay, setHighlightedDay] = useState<number | null>(null);
  const { user } = useUser();
  
  // Calculate period length based on start and end dates
  const calculatedPeriodLength = startDate && endDate 
    ? differenceInDays(endDate, startDate) + 1 
    : cycleData?.periodLength || 5;
  
  const validateDates = () => {
    if (!startDate) {
      toast.error("Please select a period start date");
      return false;
    }
    
    if (!endDate) {
      // If end date is not selected, default to startDate + 4 days
      setEndDate(addDays(startDate, 4));
      return true;
    }
    
    // Ensure end date is not before start date
    if (endDate < startDate) {
      toast.error("End date cannot be before start date");
      return false;
    }
    
    // Check if period length is reasonable (1-14 days)
    const periodLength = differenceInDays(endDate, startDate) + 1;
    if (periodLength <= 0 || periodLength > 14) {
      toast.error("Period length should be between 1 and 14 days");
      return false;
    }
    
    return true;
  };
  
  const handleSavePeriodDate = async () => {
    if (!user?.id) {
      toast.error("Please ensure you're logged in");
      return;
    }
    
    if (!validateDates()) return;
    
    // If endDate is not set, use a default of startDate + 4 days
    const finalEndDate = endDate || addDays(startDate!, 4);
    const periodLengthNum = differenceInDays(finalEndDate, startDate!) + 1;
    
    try {
      // First check if a health profile exists for this user
      const { data: existingProfile, error: profileCheckError } = await supabase
        .from('health_profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (profileCheckError) throw profileCheckError;
      
      if (!existingProfile) {
        // Create a new health profile if none exists
        const { error: createProfileError } = await supabase
          .from('health_profiles')
          .insert({ 
            user_id: user.id,
            last_period_start: format(startDate!, 'yyyy-MM-dd'),
            last_period_duration: periodLengthNum.toString(),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
        
        if (createProfileError) throw createProfileError;
      } else {
        // Update existing health profile
        const { error: updateProfileError } = await supabase
          .from('health_profiles')
          .update({ 
            last_period_start: format(startDate!, 'yyyy-MM-dd'),
            last_period_duration: periodLengthNum.toString(),
            updated_at: new Date().toISOString()
          })
          .eq('user_id', user.id);
        
        if (updateProfileError) throw updateProfileError;
      }
      
      // Then store in the period_history table
      const { error: periodHistoryError } = await supabase
        .from('period_history')
        .insert({
          user_id: user.id,
          period_start_date: format(startDate!, 'yyyy-MM-dd'),
          period_end_date: format(finalEndDate, 'yyyy-MM-dd'),
          period_length: periodLengthNum,
          cycle_length: cycleData?.cycleLength,
          data_source: 'manual',
          notes: `Period tracked from cycle tracking dashboard`
        });
      
      if (periodHistoryError) throw periodHistoryError;
      
      toast.success("💕 Period data saved successfully!");
      setIsEditDialogOpen(false);
      
      // Reload the page to reflect the changes
      setTimeout(() => window.location.reload(), 1500);
      
    } catch (error) {
      console.error('Error saving period data:', error);
      toast.error("Failed to save period data. Please try again.");
    }
  };
  
  return {
    isEditDialogOpen,
    setIsEditDialogOpen,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    activeCalendar,
    setActiveCalendar,
    highlightedDay,
    setHighlightedDay,
    calculatedPeriodLength,
    handleSavePeriodDate,
  };
};
